import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Button,
  Tooltip,
  DialogActions,
  Snackbar,
  Grid,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import LoginIcon from '@mui/icons-material/Login';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import BackspaceIcon from '@mui/icons-material/Backspace';
import { clubEntryService, ClubEntrySaleCardDto } from '../../../../../services/clubEntryService';
import { useAuth } from '../../../../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';

interface ActiveEntriesModalProps {
  open: boolean;
  onClose: () => void;
  screenId?: number;
}

const ActiveEntriesModal: React.FC<ActiveEntriesModalProps> = ({ open, onClose, screenId }) => {
  const { t } = useTranslation('salesSystem');
  const { currentUser } = useAuth();
  const [cards, setCards] = useState<ClubEntrySaleCardDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Geçiş hakkı düşürme alt-modalı (yalnızca azaltılabilir, artırılamaz)
  const [consumeTarget, setConsumeTarget] = useState<ClubEntrySaleCardDto | null>(null);
  const [consumeCount, setConsumeCount] = useState<string>('1');
  const [consumeLoading, setConsumeLoading] = useState(false);
  const [consumeError, setConsumeError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);

  const [searchTerm, setSearchTerm] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const [summary, setSummary] = useState({ totalActiveCards: 0, totalInsideCards: 0 });

  const loadActiveCards = useCallback(async (p: number, ps: number, search: string) => {
    try {
      setLoading(true);
      setError(null);

      const result = await clubEntryService.getActiveCards({
        screenId,
        page: p + 1,
        pageSize: ps,
        search: search || undefined,
      });

      setCards(result.data);
      setTotalCount(result.totalCount);
    } catch (err) {
      console.error('Aktif kartlar yüklenemedi:', err);
      setError('Aktif giriş verileri yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [screenId]);

  const loadSummary = useCallback(async () => {
    try {
      const result = await clubEntryService.getActiveCardsSummary(screenId);
      setSummary(result);
    } catch {
      /* summary hatası kritik değil */
    }
  }, [screenId]);

  useEffect(() => {
    if (open) {
      setPage(0);
      setSearchTerm('');
      setSearchQuery('');
      loadActiveCards(0, pageSize, '');
      loadSummary();
      // Modal açılınca arama alanına odaklan.
      setTimeout(() => searchInputRef.current?.focus(), 150);
    }
  }, [open, screenId, pageSize, loadActiveCards, loadSummary]);

  useEffect(() => {
    if (!open) return;
    loadActiveCards(page, pageSize, searchQuery);
  }, [page, searchQuery, open, pageSize, loadActiveCards]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(0);
      setSearchQuery(searchTerm);
    }, 400);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [searchTerm]);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRefresh = () => {
    loadActiveCards(page, pageSize, searchQuery);
    loadSummary();
  };

  const openConsume = (card: ClubEntrySaleCardDto) => {
    setConsumeTarget(card);
    setConsumeCount('');
    setConsumeError(null);
  };

  // Numpad: kalan haktan fazlasını engelleyerek rakam ekler.
  const numpadAppend = (digit: string) => {
    setConsumeError(null);
    const remaining = consumeTarget ? consumeTarget.totalEntryRights - consumeTarget.usedEntryRights : 0;
    setConsumeCount((prev) => {
      const candidate = (prev === '' || prev === '0') ? digit : prev + digit;
      const n = parseInt(candidate, 10);
      if (isNaN(n)) return prev;
      if (n > remaining) return String(remaining);
      return candidate;
    });
  };
  const numpadClear = () => { setConsumeCount(''); setConsumeError(null); };
  const numpadBackspace = () => { setConsumeCount((prev) => prev.slice(0, -1)); setConsumeError(null); };

  const handleConsume = async () => {
    if (!consumeTarget) return;
    const remaining = consumeTarget.totalEntryRights - consumeTarget.usedEntryRights;
    const count = parseInt(consumeCount, 10);
    if (!count || count < 1) {
      setConsumeError('Geçerli bir sayı girin (en az 1)');
      return;
    }
    if (count > remaining) {
      setConsumeError(`En fazla ${remaining} hak düşülebilir`);
      return;
    }
    setConsumeLoading(true);
    setConsumeError(null);
    try {
      await clubEntryService.consumeEntryRights(consumeTarget.cardUID, count, currentUser?.email || 'aktif-girisler');
      setSnackbar({ open: true, message: `${count} geçiş hakkı düşüldü`, severity: 'success' });
      setConsumeTarget(null);
      loadActiveCards(page, pageSize, searchQuery);
      loadSummary();
    } catch (err: any) {
      setConsumeError(err?.response?.data?.message || 'Geçiş hakkı düşülürken bir hata oluştu');
    }
    setConsumeLoading(false);
  };

  const getStatusChip = (card: ClubEntrySaleCardDto) => {
    if (card.status === 'ACTIVE') return <Chip size="small" label={t('activeEntriesModal.status.active')} color="success" />;
    if (card.status === 'EXPIRED') return <Chip size="small" label={t('activeEntriesModal.status.rightUsed')} color="warning" />;
    if (card.status === 'RETURNED') return <Chip size="small" label={t('activeEntriesModal.status.returned')} color="default" />;
    return <Chip size="small" label={card.status} />;
  };

  return (
    <>
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ bgcolor: 'info.main', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PeopleAltIcon />
          <Typography variant="h6">{t('activeEntriesModal.title')}</Typography>
          <Chip
            size="small"
            label={t('activeEntriesModal.summary.cards', { count: summary.totalActiveCards })}
            sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
          />
          <Chip
            size="small"
            icon={<LoginIcon sx={{ fontSize: 14, color: 'white !important' }} />}
            label={t('activeEntriesModal.summary.inside', { count: summary.totalInsideCards })}
            sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white' }}
          />
        </Box>
        <IconButton color="inherit" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 2, mt: 1 }}>
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <TextField
            fullWidth
            size="small"
            placeholder={t('activeEntriesModal.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            inputRef={searchInputRef}
            autoFocus
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Button variant="outlined" onClick={handleRefresh} disabled={loading} startIcon={<RefreshIcon />}>
            {t('activeEntriesModal.refresh')}
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : cards.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
            <PeopleAltIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
            <Typography variant="body2">
              {searchQuery ? t('activeEntriesModal.empty.search') : t('activeEntriesModal.empty.default')}
            </Typography>
          </Box>
        ) : (
          <>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'action.hover' }}>
                    <TableCell width={50}><strong>#</strong></TableCell>
                    <TableCell><strong>{t('activeEntriesModal.columns.cardUid')}</strong></TableCell>
                    <TableCell><strong>Cari</strong></TableCell>
                    <TableCell><strong>Hizmet</strong></TableCell>
                    <TableCell align="center"><strong>Geçiş Hakkı</strong></TableCell>
                    <TableCell align="center"><strong>Deposito</strong></TableCell>
                    <TableCell align="center"><strong>Dep. Ödeme</strong></TableCell>
                    <TableCell align="center"><strong>Ödeme</strong></TableCell>
                    <TableCell align="center"><strong>Konum</strong></TableCell>
                    <TableCell align="center"><strong>{t('activeEntriesModal.columns.status')}</strong></TableCell>
                    <TableCell><strong>{t('activeEntriesModal.columns.entryTime')}</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cards.map((card, index) => (
                    <TableRow key={card.id} hover>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {page * pageSize + index + 1}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">{card.cardUID}</Typography>
                        {card.cardLabel && (
                          <Typography variant="caption" color="text.secondary">{card.cardLabel}</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {card.cardAccountName
                          ? <Typography variant="body2">{card.cardAccountName}</Typography>
                          : <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>—</Typography>}
                      </TableCell>
                      <TableCell>{card.serviceProductName || '-'}</TableCell>
                      <TableCell align="center">
                        {card.entryMode === 'UNLIMITED_DAILY' ? (
                          <Chip size="small" label="Sınırsız" color="primary" />
                        ) : (() => {
                          const remaining = card.totalEntryRights - card.usedEntryRights;
                          const canConsume = remaining > 0;
                          return (
                            <Tooltip title={canConsume ? 'Geçiş hakkı düş (giren kişi sayısı)' : 'Düşülecek hak kalmadı'}>
                              <Chip
                                size="small"
                                icon={<MeetingRoomIcon sx={{ fontSize: 14 }} />}
                                label={`${remaining}/${card.totalEntryRights}`}
                                color={canConsume ? 'success' : 'default'}
                                variant={canConsume ? 'filled' : 'outlined'}
                                onClick={canConsume ? () => openConsume(card) : undefined}
                                sx={{ cursor: canConsume ? 'pointer' : 'default' }}
                              />
                            </Tooltip>
                          );
                        })()}
                      </TableCell>
                      <TableCell align="center">
                        {card.depositAmount > 0 ? (
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              ₺{card.depositAmount.toFixed(2)}
                            </Typography>
                            {card.depositRefunded && (
                            <Chip size="small" label={t('activeEntriesModal.refunded')} color="warning" sx={{ mt: 0.5, fontSize: '0.65rem', height: 20 }} />
                            )}
                          </Box>
                        ) : '-'}
                      </TableCell>
                      <TableCell align="center">
                        {card.depositAmount > 0 ? (() => {
                          // Depozito yöntemi: önce açık alan (depositPaymentMethod),
                          // yoksa eski kayıtlar için PavoPay satış no varlığına bak.
                          const isDepositPos = card.depositPaymentMethod
                            ? card.depositPaymentMethod === 'POS'
                            : !!card.depositPavoPaySaleNumber;
                          return (
                            <Chip
                              size="small"
                              label={isDepositPos ? 'POS' : t('activeEntriesModal.cash')}
                              color={isDepositPos ? 'primary' : 'default'}
                              variant="outlined"
                            />
                          );
                        })() : '-'}
                      </TableCell>
                      <TableCell align="center">
                        <Chip size="small" label={card.paymentMethod === 'POS' ? 'POS' : t('activeEntriesModal.cash')} variant="outlined" />
                      </TableCell>
                      <TableCell align="center">
                        <Box>
                          <Chip size="small"
                            label={card.isInside ? `${t('activeEntriesModal.inside')}${card.insideCount > 1 ? ` (${card.insideCount})` : ''}` : t('activeEntriesModal.outside')}
                            color={card.isInside ? 'success' : 'default'}
                            variant={card.isInside ? 'filled' : 'outlined'}
                          />
                          {card.lastPassTime && (
                            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.3, fontSize: '0.6rem' }}>
                              {card.lastPassDirection === 'entry' ? 'Son giriş' : 'Son çıkış'}: {new Date(card.lastPassTime.endsWith('Z') ? card.lastPassTime : card.lastPassTime + 'Z').toLocaleTimeString('tr-TR')}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="center">{getStatusChip(card)}</TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {new Date(card.createdAt.endsWith('Z') ? card.createdAt : card.createdAt + 'Z').toLocaleString('tr-TR')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={pageSize}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage={t('activeEntriesModal.rowsPerPage')}
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
            />
          </>
        )}
      </DialogContent>
    </Dialog>

      {/* Geçiş Hakkı Düşürme Modalı (yalnızca azaltma) */}
      <Dialog open={!!consumeTarget} onClose={() => { if (!consumeLoading) setConsumeTarget(null); }} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <RemoveCircleOutlineIcon color="warning" />
          Geçiş Hakkı Düş
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2, mt: 1 }}>
            Giren kişi sayısını girin; bu kadar geçiş hakkı düşülecektir. Hak yalnızca azaltılabilir, artırılamaz.
          </Alert>
          {consumeTarget && (
            <>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                Kart: <strong>{consumeTarget.cardUID}</strong>{consumeTarget.cardLabel ? ` — ${consumeTarget.cardLabel}` : ''}
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                Kalan geçiş hakkı: <strong>{consumeTarget.totalEntryRights - consumeTarget.usedEntryRights}</strong> / {consumeTarget.totalEntryRights}
              </Typography>
              <TextField
                fullWidth
                label="Giren kişi sayısı"
                value={consumeCount}
                placeholder="0"
                InputProps={{
                  readOnly: true,
                  sx: { fontSize: '1.8rem', fontWeight: 'bold' },
                }}
                sx={{ mb: 2, '& input': { textAlign: 'center' } }}
              />

              {/* Numpad */}
              <Grid container spacing={1}>
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
                  <Grid item xs={4} key={num}>
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={() => numpadAppend(num.toString())}
                      disabled={consumeLoading}
                      sx={{ fontSize: '1.4rem', height: 52, fontWeight: 'bold' }}
                    >
                      {num}
                    </Button>
                  </Grid>
                ))}
                <Grid item xs={4}>
                  <Button
                    fullWidth
                    variant="outlined"
                    color="error"
                    onClick={numpadClear}
                    disabled={consumeLoading}
                    sx={{ fontSize: '0.85rem', height: 52, fontWeight: 'bold' }}
                  >
                    Temizle
                  </Button>
                </Grid>
                <Grid item xs={4}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={() => numpadAppend('0')}
                    disabled={consumeLoading}
                    sx={{ fontSize: '1.4rem', height: 52, fontWeight: 'bold' }}
                  >
                    0
                  </Button>
                </Grid>
                <Grid item xs={4}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={numpadBackspace}
                    disabled={consumeLoading}
                    sx={{ height: 52 }}
                  >
                    <BackspaceIcon />
                  </Button>
                </Grid>
              </Grid>
            </>
          )}
          {consumeError && <Alert severity="error" sx={{ mt: 2 }}>{consumeError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConsumeTarget(null)} disabled={consumeLoading}>İptal</Button>
          <Button onClick={handleConsume} variant="contained" color="warning" disabled={consumeLoading} startIcon={<RemoveCircleOutlineIcon />}>
            {consumeLoading ? 'Düşülüyor...' : 'Düş'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={3500} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>{snackbar.message}</Alert>
      </Snackbar>
    </>
  );
};

export default ActiveEntriesModal;
